import * as React from 'react';

import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { formatISOTimeForDisplay, generateTimeOptions } from '../../utils/time-utils.ts';
import { Button } from './button.tsx';
import { Select, SelectContent, SelectItem, SelectTriggerNoStyling, SelectValue } from './select.tsx';

export type TimePickerProps = {
    /**
     * The current value as a time string in HH:mm:ss format
     */
    value: string | null;
    /**
     * Callback when value changes, providing the new time string in HH:mm:ss format
     */
    onChange?: (value: string) => void;
    /**
     * Placeholder for the select input
     */
    placeholder?: string;
    /**
     * Whether the component is disabled
     */
    disabled?: boolean;
    /**
     * Additional class name for styling
     */
    className?: string;
    /**
     * The minimum time to display
     */
    min?: string;
    /**
     * The maximum time to display
     */
    max?: string;
    /**
     * Minutes between two adjacent options. Pass the same value a consumer does its own time
     * arithmetic with, so its bounds land on the grid.
     */
    stepMinutes?: number;
};

const PREFERRED_TIME_OPTION = '08:00:00';

/**
 * TimePicker component for selecting time values
 */
export const TimePicker: React.FC<TimePickerProps> = ({
    value,
    onChange,
    placeholder,
    disabled = false,
    className,
    min,
    max,
    stepMinutes = 15,
}) => {
    const { t } = useLocale('common');
    const [preferredOptionElement, setPreferredOptionElement] = React.useState<HTMLDivElement | null>(null);
    const options = React.useMemo(() => generateTimeOptions(stepMinutes, min, max), [stepMinutes, min, max]);

    React.useEffect(() => {
        if (preferredOptionElement && !value) {
            setTimeout(() => {
                if (document.body.contains(preferredOptionElement)) {
                    preferredOptionElement.focus();
                }
            });
        }
    }, [preferredOptionElement, value]);

    return (
        <Select
            value={value ?? ''}
            onValueChange={(newValue) => {
                if (onChange && newValue) {
                    onChange(newValue);
                }
            }}
            disabled={disabled}
        >
            <SelectTriggerNoStyling asChild>
                <Button variant="ghost" className={cn('shrink w-auto', className)}>
                    <SelectValue placeholder={placeholder ?? t('time_picker__placeholder')} />
                </Button>
            </SelectTriggerNoStyling>
            <SelectContent>
                {options.map((time) => (
                    <SelectItem
                        key={time}
                        value={time}
                        ref={time === PREFERRED_TIME_OPTION ? setPreferredOptionElement : undefined}
                    >
                        {t('time_picker__time_option', { time: formatISOTimeForDisplay(time, 'HH:mm') })}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
};

export default TimePicker;
